/**
 * Seeded PRNG for sim layers (brief §5.1): mulberry32, drawn in a
 * deterministic order. Uses ONLY exactly-specified operations
 * (Math.imul, xor, shifts, + - * /), so it is bit-identical on every engine.
 *
 * This file is under the determinism lint fence — keep it that way.
 */

/** FNV-1a 32-bit over a string — turns an arbitrary seed string into a u32. */
export function seedFromString(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export interface Rng {
  /** Next uniform u32. */
  nextU32(): number;
  /** Integer in [0, maxExclusive). maxExclusive must be a positive integer. */
  nextInt(maxExclusive: number): number;
  /** Fraction in [0, 1) — exact division by 2^32, safe cross-engine. */
  nextFrac(): number;
  /** Snapshot of internal state (for hashing / debugging). */
  state(): number;
}

export function createRng(seed: string | number): Rng {
  let a = (typeof seed === 'number' ? seed : seedFromString(seed)) >>> 0;
  // Avoid the degenerate all-zero state.
  if (a === 0) a = 0x9e3779b9;
  const nextU32 = (): number => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t = (t + Math.imul(t ^ (t >>> 7), t | 61)) ^ t;
    return (t ^ (t >>> 14)) >>> 0;
  };
  return {
    nextU32,
    nextInt(maxExclusive: number): number {
      return nextU32() % maxExclusive;
    },
    nextFrac(): number {
      return nextU32() / 4294967296;
    },
    state(): number {
      return a;
    },
  };
}
